import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { Login } from '../components/Login';
import { TooltipProvider } from '../components/ui/tooltip';
import { AuthProvider } from '../contexts/AuthContext';
import { PsychologistProvider } from '../contexts/PsychologistContext';
import { hasValidSession } from '../hooks/api';
import { ToastContainer } from '../components/common/Toast/ToastContainer';
import { ComunidadPage } from '../pages/comunidad/ComunidadPage';
import { NewsArchivePage } from '../pages/comunidad/NewsArchivePage';
import { NewsletterArchivePage } from '../pages/comunidad/NewsletterArchivePage';
import { ComunidadGuard } from '../routes/ComunidadGuard';
import Derivaciones from '../pages/Derivaciones';
import { FarewellEmbed } from '../pages/FarewellEmbed';
import { ProfilePage } from '../pages/profile/ProfilePage';
import { PwaBoot } from './PwaBoot';
import { PwaCalendarAlias } from './PwaCalendarAlias';
import { PwaHome } from './PwaHome';
import { PwaInicio } from './PwaInicio';
import { PwaLoginLinkGate } from './PwaLoginLinkGate';
import { PwaModeProvider } from './PwaModeContext';
import { PwaPacientes } from './PwaPacientes';
import { PwaNotificacionesRedirect } from './PwaNotificacionesRedirect';
import { PwaShell } from './PwaShell';

/** Todo lo que no es el login exige sesión; sin ella, al login (que vuelve acá después de ingresar). */
function PwaProtected() {
  const location = useLocation();
  if (!hasValidSession()) return <Navigate to="/login" state={{ from: location }} replace />;
  return <Outlet />;
}

/**
 * Raíz de la app cuando corre instalada como PWA en un celular. Deliberadamente NO monta `App` ni `AppRoutes`:
 * expone solo el login y sus secciones — Home (la de la web, solo si la psicóloga tiene acceso), Agenda (se puede agendar,
 * reprogramar y cancelar; es la pantalla a la que se entra), Pacientes, Comunidad (los mismos módulos de la plataforma web;
 * Comunidad sin su panel de admin) y Perfil (el de la web, sin la conexión con Google Calendar, y con Notificaciones —activar
 * las push— como una sección más; se abre desde la barra de arriba). `/` es la pantalla de carga y entra a la Agenda. Cualquier
 * otra ruta vuelve a `/`.
 * Para sumar una sección: una ruta acá y un ítem en `PwaShell` — no reutilizar `AppRoutes`.
 */
export function PwaApp() {
  return (
    <PwaModeProvider value>
      <BrowserRouter>
        <AuthProvider>
          <PsychologistProvider>
            <TooltipProvider>
              <PwaLoginLinkGate>
                <Routes>
                  <Route path="/login" element={<Login />} />
                  <Route element={<PwaProtected />}>
                    {/* `/` es la pantalla de carga (la misma de la web) y entra a la Agenda; va fuera del marco para verse a pantalla completa. */}
                    <Route path="/" element={<PwaBoot />} />
                    <Route element={<PwaShell />}>
                      <Route path="/home" element={<PwaHome />} />
                      <Route path="/calendar" element={<PwaInicio />} />
                      {/* Notificaciones pasó a ser una sección de Perfil; se conserva la ruta vieja por los links de push ya enviados. */}
                      <Route path="/notificaciones" element={<PwaNotificacionesRedirect />} />
                      <Route path="/profile" element={<ProfilePage />} />
                      <Route path="/profile/farewell" element={<FarewellEmbed />} />
                      <Route path="/profile/metricas/:subtab" element={<ProfilePage />} />
                      <Route path="/profile/metricas/:subtab/:section" element={<ProfilePage />} />
                      <Route path="/profile/:tab" element={<ProfilePage />} />
                      {/* Slugs de vista de la Agenda de la WEB (/calendar/semanal…) → la Agenda de la app (/calendar?view=), conservando el query. */}
                      <Route path="/calendar/:view" element={<PwaCalendarAlias />} />
                      <Route path="/patients" element={<PwaPacientes />} />
                      <Route path="/patients/derivation" element={<Derivaciones />} />
                      <Route path="/patients/:status" element={<PwaPacientes />} />
                      <Route element={<ComunidadGuard />}>
                        <Route path="/comunidad" element={<ComunidadPage />} />
                        <Route path="/comunidad/newsletter" element={<NewsletterArchivePage />} />
                        <Route path="/comunidad/novedades/:feedSlug" element={<NewsArchivePage />} />
                      </Route>
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Route>
                  </Route>
                </Routes>
              </PwaLoginLinkGate>
              <ToastContainer />
            </TooltipProvider>
          </PsychologistProvider>
        </AuthProvider>
      </BrowserRouter>
    </PwaModeProvider>
  );
}
